import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgModule} from '@angular/core';
import {ActivePromotionConfig} from './config/active-promotion.config';
import {HashLocationStrategy, LocationStrategy} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';

import {AppComponent} from './app.component';
import {AdminContainerComponent} from './containers/admin-container.component';
import {UserContainerComponent} from './containers/user-container.component';

const APP_CONTAINERS = [
    AdminContainerComponent,
    UserContainerComponent
];

// vendored CoreUI (app-header + sidebar toggler のみ。src/app/lib/coreui)
import {AppHeaderModule} from '@coreui/angular';

// Import routing module
import {AppRoutingModule} from './app.routing';

// Import 3rd party components
import {EditorModule, TINYMCE_SCRIPT_SRC} from '@tinymce/tinymce-angular';
import {AuthEffects, AuthModule} from '@cosbeni/auth';
import {StoreModule} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {CollapseModule} from 'ngx-bootstrap/collapse';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {ModalModule} from 'ngx-bootstrap/modal';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {TabsModule} from 'ngx-bootstrap/tabs';
import {WjCoreModule} from '@mescius/wijmo.angular2.core';
import {WjInputModule} from '@mescius/wijmo.angular2.input';
import {WjGridModule} from '@mescius/wijmo.angular2.grid';
import {WjGridFilterModule} from '@mescius/wijmo.angular2.grid.filter';
import {WjChartModule} from '@mescius/wijmo.angular2.chart';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {WjOlapModule} from '@mescius/wijmo.angular2.olap';
import {WjGridMultirowModule} from '@mescius/wijmo.angular2.grid.multirow';
import {FullCalendarModule} from '@fullcalendar/angular';
// vendored angular2-toaster 互換 (src/app/ui/toaster)
import {ToasterModule} from 'angular2-toaster';
import {FormHelperService} from './services/form-helper.service';
import {AdminGuard} from './services/admin.guard';
import {AuthGuard} from './services/auth.guard';
import {CapabilityGuard} from './services/capability.guard';
import {GapiSession} from './gapi/gapi.session';

import {ChangePasswordComponent} from './pages/change-password.component';
import {LoginComponent} from './pages/login.component';
import {ManualComponent} from './manual/manual.component';
import {ResetPasswordComponent} from './pages/reset-password.component';

import * as fromActivePromotion from './ngrx/reducers/index';
import {DatabaseEffects, DatabaseService, DataService} from '@cosbeni/database';
import {ADMIN_COMPONENTS} from './admin';
import {USER_COMPONENTS} from './user';
import {KOBEAC_COMPONENTS} from './kobeac';
import {KAITORIYA_COMPONENTS} from './kaitoriya';
import {SYSTEM_COMPONENTS} from './system';
import {WorkspaceNavComponent} from './shared/workspace-nav.component';
import {FlexGridAutoSizeDirective} from './shared/flex-grid-autosize.directive';
import {LoadingService} from './services/loading.service';
import {WebsocketOneService} from './services/websocket.one.service';
import {WebsocketTwoService} from './services/websocket.two.service';
import {LoadingInterceptor} from './services/loading.interceptor';
import {ConfirmComponent} from './ui/modal';
import {SpinnerComponent} from './ui/spinner/spinner.component';
import {UserEffects} from './ngrx/effects';
import {BreadcrumbsComponent} from './ui/breadcrumb/breadcrumb.component';
import {WhiteboardComponent} from './ui/whiteboard/whiteboard.component';
import {NgxImageZoomModule} from './ui/zoom/ngx-image-zoom.module';
import {GalleryModule} from './ui/gallery/gallery.module';
import {TranslocoRootModule} from './i18n/transloco-root.module';
import {LanguagesComponent} from './i18n/languages.component';

export function initGapi(gapiSession: GapiSession) {
    return () => gapiSession.initClient();
}

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AppHeaderModule,
        // serverUrl の既定は 'http://localhost/server'。OrientDBCollectionView は
        // コンストラクタで即クエリするため、.url 設定前は既定 (localhost) に飛ぶ。
        // アプリの API host を既定に上書きして全 dataService 呼び出しを正しい向き先にする。
        AuthModule.forRoot({serverUrl: ActivePromotionConfig.SERVER_URL}),
        CollapseModule.forRoot(),
        EditorModule,
        FormsModule,
        ReactiveFormsModule,
        GalleryModule,
        TranslocoRootModule,
        BsDropdownModule.forRoot(),
        ModalModule.forRoot(),
        PaginationModule.forRoot(),
        NgxImageZoomModule,
        TabsModule.forRoot(),
        TooltipModule.forRoot(),
        WjCoreModule,
        WjInputModule,
        WjGridModule,
        WjGridFilterModule,
        WjChartModule,
        WjNavModule,
        FullCalendarModule,
        WjOlapModule,
        WjGridMultirowModule,
        ToasterModule.forRoot(),
        StoreModule.forRoot({}, {
                runtimeChecks: {
                    strictStateImmutability: false,
                    strictActionImmutability: false,
                }
            }
        ),
        StoreModule.forFeature('activePromotionFeature', fromActivePromotion.reducer),
        EffectsModule.forRoot([AuthEffects, DatabaseEffects, UserEffects]),
    ],
    declarations: [
        AppComponent,
        ChangePasswordComponent,
        LoginComponent,
        ManualComponent,
        ResetPasswordComponent,
        ...APP_CONTAINERS,
        ADMIN_COMPONENTS,
        USER_COMPONENTS,
        KOBEAC_COMPONENTS,
        KAITORIYA_COMPONENTS,
        SYSTEM_COMPONENTS,
        WorkspaceNavComponent,
        FlexGridAutoSizeDirective,
        BreadcrumbsComponent,
        ConfirmComponent,
        SpinnerComponent,
        WhiteboardComponent,
        LanguagesComponent
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy
        },
        {provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true},
        {provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js'},
        FormHelperService,
        AdminGuard,
        AuthGuard,
        CapabilityGuard,
        DataService,
        DatabaseService,
        GapiSession,
        LoadingService,
        WebsocketOneService,
        WebsocketTwoService,
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
